import { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

interface WhatsAppOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function WhatsAppOrderModal({ isOpen, onClose }: WhatsAppOrderModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    quantity: 1,
    address: '',
    customerType: 'occasional',
    deliveryDay: 'today',
    deliveryTime: ''
  });
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'verifying' | 'verified' | 'failed'>('idle');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [verifiedCustomer, setVerifiedCustomer] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (formData.customerType === 'occasional') {
      setVerificationStatus('idle');
      setVerificationMessage('');
      setVerifiedCustomer(null);
      setErrorMessage('');
    }
  }, [formData.customerType]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.customerType !== 'occasional' && formData.name && formData.phone) {
        verifyCustomer();
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [formData.name, formData.phone, formData.customerType]);

  const verifyCustomer = async () => {
    if (formData.customerType === 'occasional') return;

    const trimmedName = formData.name.trim();
    const trimmedPhone = formData.phone.replace(/\s+/g, '').replace(/\D/g, '');

    if (trimmedName.length < 2 && trimmedPhone.length < 6) return;

    setVerificationStatus('verifying');
    
    const { data: customers, error } = await supabase
      .from('customers')
      .select('*')
      .eq('customer_type', formData.customerType)
      .eq('status', 'active');

    if (error || !customers || customers.length === 0) {
      setVerificationStatus('failed');
      setVerificationMessage('No hay clientes registrados con este tipo.');
      setVerifiedCustomer(null);
      return;
    }

    const normalizedName = trimmedName.toLowerCase();
    const normalizedPhone = trimmedPhone;

    let matchedCustomer = customers.find(customer => {
      const customerPhone = customer.phone.replace(/\s+/g, '').replace(/\D/g, '');
      return normalizedPhone.length >= 6 && customerPhone === normalizedPhone;
    });

    if (!matchedCustomer && normalizedName.length >= 3) {
      matchedCustomer = customers.find(customer => {
        const customerName = customer.name.toLowerCase().trim();
        return customerName === normalizedName ||
               customerName.includes(normalizedName) ||
               normalizedName.includes(customerName);
      });
    }

    if (matchedCustomer) {
      setVerificationStatus('verified');
      setVerificationMessage(`✓ Cliente verificado: ${matchedCustomer.name}`);
      setVerifiedCustomer(matchedCustomer);
    } else {
      setVerificationStatus('failed');
      const typeLabel = formData.customerType === 'store' ? 'tienda' : 'cliente registrado';
      setVerificationMessage(`No se encontró ningún ${typeLabel} registrado con ese nombre o teléfono. Se aplicará el precio de cliente ocasional.`);
      setVerifiedCustomer(null);
    }
  };

  if (!isOpen) return null;

  const getPriceByType = (type: string) => {
    if (type === 'occasional') return 17000;
    if (verificationStatus !== 'verified') return 17000;
    
    switch (type) {
      case 'store': return 12000;
      default: return 17000;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const sendEmailNotification = (saleData: any) => {
    const deliveryDayText = formData.deliveryDay === 'today' ? 'Hoy' : 'Mañana';
    const customerTypeText = formData.customerType === 'store' ? 'Tienda' : 'Cliente Ocasional';

    const emailBody = [
      `Nuevo pedido de agua PUREZA`,
      ``,
      `Cliente: ${formData.name}`,
      `Teléfono: ${formData.phone}`,
      `Tipo: ${customerTypeText}`,
      `Cantidad: ${formData.quantity} botellón(es)`,
      `Precio unitario: ${formatCurrency(saleData.unit_price)}`,
      `Total: ${formatCurrency(saleData.total_amount)}`,
      `Dirección: ${formData.address}`,
      `Entrega: ${deliveryDayText} a las ${formData.deliveryTime}`,
      `Estado: PENDIENTE`
    ].join('\n');

    const formDataEmail = new URLSearchParams();
    formDataEmail.append('email', 'charubano@gmail.com');
    formDataEmail.append('subject', `Nuevo pedido PUREZA - ${formData.name}`);
    formDataEmail.append('message', emailBody);

    fetch('https://readdy.ai/api/form/d7vljq53mj78tu7vo99g', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formDataEmail.toString()
    }).catch(() => {
      // Silenciar errores de email, el pedido ya fue guardado
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    if (formData.customerType !== 'occasional' && verificationStatus !== 'verified') {
      setErrorMessage('Debes ser un cliente registrado para acceder a este precio. Por favor verifica tus datos o selecciona "Cliente Ocasional".');
      return;
    }

    setIsSubmitting(true);
    
    const price = getPriceByType(formData.customerType);
    const total = price * formData.quantity;
    
    const deliveryDayText = formData.deliveryDay === 'today' ? 'Hoy' : 'Mañana';

    const saleData = {
      customer_name: formData.name,
      customer_phone: formData.phone,
      customer_type: formData.customerType,
      quantity: formData.quantity,
      unit_price: price,
      total_amount: total,
      payment_method: 'pending',
      sale_origin: 'website',
      status: 'pending',
      payment_status: 'pending',
      delivery_address: formData.address,
      delivery_day: formData.deliveryDay,
      delivery_time: formData.deliveryTime,
      notes: `Pedido web - ${deliveryDayText} a las ${formData.deliveryTime}${verificationStatus === 'verified' ? ' - Cliente verificado' : ''}`
    };

    try {
      const { error } = await supabase.from('sales').insert([saleData]);
      
      if (error) {
        console.error('Error al registrar venta:', error);
        setErrorMessage('Hubo un error al guardar el pedido. Intenta de nuevo.');
        setIsSubmitting(false);
        return;
      }

      // Enviar notificación por email
      sendEmailNotification(saleData);

      // Mostrar pantalla de éxito
      setShowSuccess(true);

      // Cerrar modal y limpiar después de 4 segundos
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        setFormData({
          name: '',
          phone: '',
          quantity: 1,
          address: '',
          customerType: 'occasional',
          deliveryDay: 'today',
          deliveryTime: ''
        });
        setVerificationStatus('idle');
        setVerificationMessage('');
        setVerifiedCustomer(null);
        setErrorMessage('');
        setIsSubmitting(false);
      }, 4000);

    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('Hubo un error inesperado. Intenta de nuevo.');
      setIsSubmitting(false);
    }
  };

  const currentPrice = getPriceByType(formData.customerType);
  const currentTotal = currentPrice * formData.quantity;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-green-600 to-green-500">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
              <i className="ri-shopping-cart-line text-green-600 text-2xl"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Hacer Pedido</h3>
              <p className="text-sm text-green-50">Pedido en línea</p>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="text-white hover:text-green-100 transition-colors"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6">
          {showSuccess ? (
            /* Pantalla de éxito */
            <div className="text-center py-8 space-y-5">
              <div className="w-24 h-24 mx-auto bg-gradient-to-br from-green-500 to-emerald-400 rounded-full flex items-center justify-center animate-bounce">
                <i className="ri-check-line text-white text-5xl"></i>
              </div>
              <div>
                <h4 className="text-2xl font-bold text-gray-900 mb-2">¡Pedido recibido!</h4>
                <p className="text-gray-600">Tu pedido fue guardado correctamente.</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-left space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cliente:</span>
                  <span className="font-semibold text-gray-900">{formData.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cantidad:</span>
                  <span className="font-semibold text-gray-900">{formData.quantity} botellón(es)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total:</span>
                  <span className="font-bold text-green-600">{formatCurrency(currentTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Entrega:</span>
                  <span className="font-semibold text-gray-900">
                    {formData.deliveryDay === 'today' ? 'Hoy' : 'Mañana'} a las {formData.deliveryTime}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                Te contactaremos pronto para confirmar la entrega. Cerrando...
              </p>
            </div>
          ) : (
            /* Formulario */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <i className="ri-information-line text-green-600 text-xl mt-0.5"></i>
                  <div className="text-sm text-green-800">
                    <p className="font-semibold mb-1">¡Pedido fácil y rápido!</p>
                    <p>Completa el formulario y guardaremos tu pedido. Te contactaremos para confirmar la entrega.</p>
                  </div>
                </div>
              </div>

              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-error-warning-fill text-red-600 text-xl mt-0.5"></i>
                    <div className="text-sm text-red-800">
                      <p className="font-semibold">Verificación requerida</p>
                      <p className="mt-1">{errorMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre completo *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Juan Pérez"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono de contacto *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="Ej: 300 123 4567"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de cliente *
                </label>
                <select
                  value={formData.customerType}
                  onChange={(e) => setFormData({...formData, customerType: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                >
                  <option value="occasional">Cliente Ocasional - {formatCurrency(17000)}</option>
                  <option value="store">Tienda - {formatCurrency(12000)}</option>
                </select>
              </div>

              {formData.customerType !== 'occasional' && (
                <div className={`rounded-lg p-4 border ${
                  verificationStatus === 'verifying' ? 'bg-blue-50 border-blue-200' :
                  verificationStatus === 'verified' ? 'bg-green-50 border-green-200' :
                  verificationStatus === 'failed' ? 'bg-red-50 border-red-200' :
                  'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-start gap-3">
                    {verificationStatus === 'verifying' && (
                      <>
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mt-0.5"></div>
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold">Verificando cliente...</p>
                        </div>
                      </>
                    )}
                    {verificationStatus === 'verified' && (
                      <>
                        <i className="ri-checkbox-circle-fill text-green-600 text-xl mt-0.5"></i>
                        <div className="text-sm text-green-800">
                          <p className="font-semibold">{verificationMessage}</p>
                          <p className="mt-1">Puedes acceder al precio especial de {formData.customerType === 'store' ? 'tienda' : 'cliente'}.</p>
                        </div>
                      </>
                    )}
                    {verificationStatus === 'failed' && (
                      <>
                        <i className="ri-error-warning-fill text-red-600 text-xl mt-0.5"></i>
                        <div className="text-sm text-red-800">
                          <p className="font-semibold">{verificationMessage}</p>
                          <p className="mt-1">Se aplicará el precio de cliente ocasional ({formatCurrency(17000)}).</p>
                        </div>
                      </>
                    )}
                    {verificationStatus === 'idle' && (
                      <>
                        <i className="ri-information-line text-gray-600 text-xl mt-0.5"></i>
                        <div className="text-sm text-gray-700">
                          <p className="font-semibold">Verificación requerida</p>
                          <p className="mt-1">Ingresa tu nombre y teléfono registrados para acceder a este precio.</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cantidad de botellones *
                </label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, quantity: Math.max(1, formData.quantity - 1)})}
                    className="w-10 h-10 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center"
                  >
                    <i className="ri-subtract-line text-xl"></i>
                  </button>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.quantity}
                    onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent text-center font-semibold text-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, quantity: formData.quantity + 1})}
                    className="w-10 h-10 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors flex items-center justify-center"
                  >
                    <i className="ri-add-line text-xl"></i>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dirección de entrega *
                </label>
                <textarea
                  required
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Ej: Calle 123 #45-67, Barrio Centro, Apartamento 301"
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                ></textarea>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Día de entrega *
                  </label>
                  <select
                    value={formData.deliveryDay}
                    onChange={(e) => setFormData({...formData, deliveryDay: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                  >
                    <option value="today">Hoy</option>
                    <option value="tomorrow">Mañana</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hora de entrega *
                  </label>
                  <input
                    type="time"
                    required
                    value={formData.deliveryTime}
                    onChange={(e) => setFormData({...formData, deliveryTime: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Precio unitario:</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(currentPrice)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-gray-900">Total a pagar:</span>
                  <span className="text-2xl font-bold text-green-600">{formatCurrency(currentTotal)}</span>
                </div>
                {formData.customerType !== 'occasional' && verificationStatus === 'failed' && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <p className="text-xs text-gray-600">* Precio de cliente ocasional aplicado por falta de verificación</p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex-1 px-6 py-4 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-lg hover:from-green-700 hover:to-green-600 transition-all whitespace-nowrap font-semibold flex items-center justify-center gap-2 shadow-lg ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-xl"></i>
                      Enviar Pedido
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-6 py-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}